import React from 'react';
import {
    Box,
    Paper,
    Typography,
    IconButton,
    Button,
    Stack,
    Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import dayjs from 'dayjs';
import { BackendPayload, FieldDef, FormFieldType } from '../../types/forms';
import useFormulaRunner from '../../hooks/useFormulaRunner';

type RowId = string | number;
export type FormResult = {
                             id: RowId;
                             userEmail: string;
                             date: string;
                         } & Record<string, any>;

type SingleResultViewerProps = {
    config: BackendPayload;
    data: FormResult[];
    initialIndex?: number;
    onEdit?: (id: RowId) => void;
    onDelete?: (id: RowId) => void;
    placeholder?: string;
    hasActions?: boolean;
    width?: number | string;
    maxHeight?: number | string;
};

const isEmptyLike = (v: any) =>
    v === null || v === undefined || v === '' || (
        Array.isArray(v) && v.length === 0
    );

const formatValue = (type: FormFieldType, raw: any, placeholder: string): React.ReactNode => {
    if (isEmptyLike(raw)) {
        return <Typography variant="body2" color="text.disabled">{placeholder}</Typography>;
    }
    if (type === 'date') {
        const d = dayjs(raw);
        return d.isValid() ? d.format('h:mma MM/DD/YY').toUpperCase() : String(raw);
    }
    if (type === 'color') {
        const v = String(raw);
        return (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{
                    width: 16, height: 16, borderRadius: 0.5, border: '1px solid rgba(0,0,0,0.2)', background: v,
                }}/>
                <Typography variant="body2">{v}</Typography>
            </Box>
        );
    }
    if (Array.isArray(raw)) {
        return raw.length ? JSON.stringify(raw) : placeholder;
    }
    if (typeof raw === 'object') {
        return Object.keys(raw).length ? JSON.stringify(raw) : placeholder;
    }
    return String(raw);
};

const fieldLabel = (f: FieldDef) => f.label ?? f.name;

const FormSingleResult: React.FC<SingleResultViewerProps> = ({
    config,
    data,
    initialIndex = 0,
    onEdit,
    onDelete,
    placeholder = '-',
    hasActions = false,
    width = 'max-content',
    maxHeight = 640,
}) => {
    const formulaRunner = useFormulaRunner();
    const [ index, setIndex ] = React.useState(Math.min(Math.max(0, initialIndex), Math.max(0, data.length - 1)));
    const record = data[index];

    const fieldMap = React.useMemo(() => {
        const map = new Map<string, FieldDef>();
        config.fields.forEach(f => map.set(f.name, f));
        return map;
    }, [ config.fields ]);

    const columns = React.useMemo(() => config.fields.map(f => f.name), [ config.fields ]);

    const prev = () => setIndex(i => Math.max(0, i - 1));
    const next = () => setIndex(i => Math.min(data.length - 1, i + 1));

    const getFormulaResult = (formula: string, data: any): string => {
        const mergedData = {};
        Object.keys(data).forEach(key => {
            // @ts-ignore
            mergedData[key] = { value: data[key], points: 1 };
        });
        const result = formulaRunner(formula, mergedData);
        console.log(result);
        return result as string;
    };


    if (!record) {
        return (
            <Paper sx={{ p: 2, width: 'fit-content', display: 'inline-block', maxWidth: '100%' }}>
                <Typography variant="body2" color="text.secondary">No results</Typography>
            </Paper>
        );
    }

    return (
        <Paper
            sx={{
                width: 'fit-content',
                maxWidth: '100%',
                border: 1,
                borderColor: 'divider',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box sx={{ p: 2, overflow: 'auto', maxHeight }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '220px 1fr', rowGap: 1.25, columnGap: 2, mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>Id</Typography>
                    <Typography variant="body2" color="text.secondary">{record.id}</Typography>
                </Box>

                <Divider sx={{ my: 1.5 }}/>

                <Box sx={{ display: 'grid', gridTemplateColumns: '220px 1fr', rowGap: 1.25, columnGap: 2 }}>
                    {columns.map((col) => {
                        const def = fieldMap.get(col);
                        const type: FormFieldType = def?.type ?? 'text';
                        const label = def ? fieldLabel(def) : col;
                        let val = (
                            record as any
                        )[col];

                        if (type === 'formula') {
                            return (
                                <React.Fragment key={col}>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{label}</Typography>
                                    <Box sx={{
                                        minHeight: 24, display: 'flex', alignItems: 'center',
                                        textTransform: 'capitalize',
                                    }}>
                                        {getFormulaResult(def?.formula || '', record)}
                                    </Box>
                                </React.Fragment>
                            );
                        }

                        return (
                            <React.Fragment key={col}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{label}</Typography>
                                <Box sx={{
                                    minHeight: 24, display: 'flex', alignItems: 'center', textTransform: 'capitalize',
                                }}>
                                    {formatValue(type, val, placeholder)}
                                </Box>
                            </React.Fragment>
                        );
                    })}
                </Box>
            </Box>

            <Box
                sx={{
                    px: 2,
                    py: 1.25,
                    bgcolor: '#013b71',
                    color: 'primary.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                }}
            >
                <Stack direction="row" spacing={1} alignItems="center">
                    <IconButton size="small" onClick={prev} disabled={index === 0}
                                sx={{ color: 'primary.contrastText' }}>
                        <ArrowBackIosNewIcon fontSize="small"/>
                    </IconButton>
                    <IconButton size="small" onClick={next} disabled={index === data.length - 1}
                                sx={{ color: 'primary.contrastText' }}>
                        <ArrowForwardIosIcon fontSize="small"/>
                    </IconButton>
                    <Typography variant="body2" sx={{ ml: 1 }}>
                        {index + 1} / {data.length}
                    </Typography>
                </Stack>

                {hasActions && (
                    <Stack direction="row" spacing={1}>
                        {onEdit && (
                            <Button
                                size="small"
                                onClick={() => onEdit(record.id)}
                                startIcon={<EditIcon/>}
                                sx={{ color: 'primary.contrastText' }}
                            >
                                Edit
                            </Button>
                        )}
                        {onDelete && (
                            <Button
                                size="small"
                                color="error"
                                onClick={() => onDelete(record.id)}
                                startIcon={<DeleteIcon/>}
                            >
                                Delete
                            </Button>
                        )}
                    </Stack>
                )}
            </Box>
        </Paper>
    );
};

export default FormSingleResult;